import { Pipe, PipeTransform } from "@angular/core";
import { CardModel } from "../../../swagger/api.client.generated";
import { FilterOption } from "../dialog-card-picker/dialog-card-picker.model";
import { HelperFunctions } from "../functions/helper.functions";

/**
 * Constants for better maintainability and readability
 * These map to the SafetyCardCategoryId values in the subtype model (1-6).
 * (Legacy CardCategoryId 9-14 were re-IDed to 1-6 in Migration1.)
 */
const CATEGORY_IDS = {
  FEELINGS: 1,
  THOUGHTS: 2,
  NOVELTY: 3,
  BODY: 4,
  RELATIONSHIPS: 5,
  EXPERIENCES: 6,
} as const;

/**
 * Deck type constants to avoid magic numbers
 * These correspond to different types of card decks in the system
 */
const DECK_TYPE_IDS = {
  USER_DEFINED: 1, // User-created custom cards
  ANGEL: 2, // Angel deck cards
  ALL: 3, // All cards (mixed deck)
} as const;

/**
 * Threshold separating real safety categories from cards with no category.
 * Safety cards carry SafetyCardCategoryId 1-6; activity cards carry 0.
 */
const CATEGORY_THRESHOLD = 0;

/**
 * Angular pipe for filtering cards based on multiple criteria
 *
 * This pipe implements a two-stage filtering system:
 * 1. Category filters (additive) - combines cards from multiple selected categories
 * 2. Additional filters (additive) - applies favorites/custom filters to category results
 *
 * The filtering logic ensures that additional filters only apply to cards
 * that have already passed the category filter stage.
 */
@Pipe({
  name: "cardFilter2",
  pure: true, // Pure pipe for better performance - only recalculates when inputs change
})
export class CardFilter2Pipe implements PipeTransform {
  /**
   * Cached set of category filter options for efficient lookup
   * Used to quickly identify which filter options are category-based
   */
  private readonly categoryFilterOptions = new Set([
    FilterOption.Body,
    FilterOption.Experiences,
    FilterOption.Feelings,
    FilterOption.Novelty,
    FilterOption.Relationships,
    FilterOption.Thoughts,
  ]);

  /**
   * Cached set of additional filter options for efficient lookup
   * Used to quickly identify which filter options are additional filters (favorites, custom)
   */
  private readonly additionalFilterOptions = new Set([
    FilterOption.Custom,
    FilterOption.Favorites,
  ]);

  /**
   * Main transform method that applies all filtering logic
   *
   * @param cards - Array of cards to filter
   * @param filterOptions - Array of filter options to apply
   * @param deckType - Type of deck to filter by (optional)
   * @returns Filtered array of cards
   */
  transform(
    cards: CardModel[],
    filterOptions: FilterOption[],
    deckType: number
  ): CardModel[] {
    // Early return for empty input
    if (!cards?.length) {
      return [];
    }

    // Apply deck type filtering first (if specified)
    let cardList = this.applyDeckTypeFilter([...cards], deckType);

    // Early return if no filter options provided
    if (!filterOptions?.length) {
      return cardList;
    }

    // Separate category filters from additional filters for two-stage processing
    const categoryFilters = filterOptions.filter((option) =>
      this.categoryFilterOptions.has(option)
    );
    const additionalFilters = filterOptions.filter((option) =>
      this.additionalFilterOptions.has(option)
    );

    // Stage 1: Apply category filters additively (combine all selected categories)
    const categoryFilteredCards = this.applyCategoryFilters(
      cardList,
      categoryFilters
    );

    // Stage 2: Apply additional filters to the category-filtered results
    return this.applyAdditionalFilters(
      categoryFilteredCards,
      additionalFilters
    );
  }

  /**
   * Applies deck type filtering based on the specified deck type
   *
   * Different deck types have different filtering rules:
   * - User Defined (1): Includes user cards and specific categories from ALL deck
   * - Angel (2): Includes angel cards with higher category IDs (>8)
   * - Other: Includes cards with lower category IDs (<8)
   *
   * @param cards - Cards to filter
   * @param deckType - Type of deck to filter by
   * @returns Filtered cards based on deck type
   */
  private applyDeckTypeFilter(
    cards: CardModel[],
    deckType: number
  ): CardModel[] {
    // If no deck type specified, return all cards
    if (!deckType) {
      return cards;
    }

    switch (deckType) {
      case DECK_TYPE_IDS.USER_DEFINED:
        // User defined deck: include user cards OR ALL deck cards with specific categories
        return cards.filter(
          (card) =>
            card.cardDeckType.cardDeckId === deckType ||
            (card.cardDeckType.cardDeckId === DECK_TYPE_IDS.ALL &&
              (card.cardCategory.cardCategoryId === 1 ||
                card.cardCategory.cardCategoryId === 7))
        );

      case DECK_TYPE_IDS.ANGEL:
        // Angel deck: include angel cards with higher category IDs OR ALL deck cards with higher category IDs
        return cards.filter(
          (card) =>
            (card.cardDeckType.cardDeckId === deckType &&
              card.cardCategory.cardCategoryId > CATEGORY_THRESHOLD) ||
            (card.cardDeckType.cardDeckId === DECK_TYPE_IDS.ALL &&
              card.cardCategory.cardCategoryId > CATEGORY_THRESHOLD)
        );

      default:
        // Other deck types: include cards with lower category IDs
        return cards.filter(
          (card) =>
            card.cardDeckType.cardDeckId === deckType &&
            card.cardCategory.cardCategoryId < CATEGORY_THRESHOLD
        );
    }
  }

  /**
   * Applies category filters additively (combines results from multiple categories)
   *
   * This method implements additive filtering where selecting multiple categories
   * will show cards from ALL selected categories, not just the intersection.
   *
   * @param cards - Cards to filter
   * @param categoryFilters - Array of category filter options to apply
   * @returns Combined cards from all selected categories (with duplicates removed)
   */
  private applyCategoryFilters(
    cards: CardModel[],
    categoryFilters: FilterOption[]
  ): CardModel[] {
    // If no category filters, return all cards
    if (!categoryFilters.length) {
      return cards;
    }

    // Use Set for efficient deduplication when combining multiple category results
    const uniqueCards = new Set<CardModel>();

    // Apply each category filter and add results to the set
    for (const filterOption of categoryFilters) {
      const filteredCards = this.applyCategoryFilter(cards, filterOption);
      filteredCards.forEach((card) => uniqueCards.add(card));
    }

    // Convert Set back to array
    return Array.from(uniqueCards);
  }

  /**
   * Applies additional filters (favorites, custom) additively to the provided cards
   *
   * This method applies additional filters to cards that have already been
   * filtered by category. Multiple additional filters are combined additively.
   *
   * @param cards - Cards to filter (should already be category-filtered)
   * @param additionalFilters - Array of additional filter options to apply
   * @returns Combined cards from all selected additional filters (with duplicates removed)
   */
  private applyAdditionalFilters(
    cards: CardModel[],
    additionalFilters: FilterOption[]
  ): CardModel[] {
    // If no additional filters, return cards as-is
    if (!additionalFilters.length) {
      return cards;
    }

    // Use Set for efficient deduplication when combining multiple additional filter results
    const uniqueCards = new Set<CardModel>();

    // Apply each additional filter and add results to the set
    for (const filterOption of additionalFilters) {
      const filteredCards = this.applyAdditionalFilter(cards, filterOption);
      filteredCards.forEach((card) => uniqueCards.add(card));
    }

    // Convert Set back to array
    return Array.from(uniqueCards);
  }

  /**
   * Applies a single category filter to the provided cards
   *
   * Maps the FilterOption enum value to the corresponding category ID
   * and filters cards based on that category ID.
   *
   * @param cardList - Cards to filter
   * @param filterOption - Category filter option to apply
   * @returns Cards that match the specified category
   */
  private applyCategoryFilter(
    cardList: CardModel[],
    filterOption: FilterOption
  ): CardModel[] {
    // Map FilterOption enum values to their corresponding category IDs
    const categoryIdMap: Partial<Record<FilterOption, number>> = {
      [FilterOption.Body]: CATEGORY_IDS.BODY,
      [FilterOption.Experiences]: CATEGORY_IDS.EXPERIENCES,
      [FilterOption.Feelings]: CATEGORY_IDS.FEELINGS,
      [FilterOption.Novelty]: CATEGORY_IDS.NOVELTY,
      [FilterOption.Relationships]: CATEGORY_IDS.RELATIONSHIPS,
      [FilterOption.Thoughts]: CATEGORY_IDS.THOUGHTS,
    };

    // Get the target category ID for the filter option
    const targetCategoryId = categoryIdMap[filterOption];
    if (targetCategoryId === undefined) {
      // If no mapping found, return all cards (shouldn't happen with valid category filters)
      return cardList;
    }

    // Filter cards by the target category ID
    return cardList.filter(
      (card) => card.cardCategory.cardCategoryId === targetCategoryId
    );
  }

  /**
   * Applies a single additional filter to the provided cards
   *
   * Handles favorites and custom filters based on card properties.
   *
   * @param cardList - Cards to filter
   * @param filterOption - Additional filter option to apply
   * @returns Cards that match the specified additional filter criteria
   */
  private applyAdditionalFilter(
    cardList: CardModel[],
    filterOption: FilterOption
  ): CardModel[] {
    switch (filterOption) {
      case FilterOption.Favorites:
        // Return only cards marked as patient favorites
        return cardList.filter((card) => card.isPatientFavourite);
      case FilterOption.Custom:
        // Return cards that are user-defined, patient deck, or editable
        return cardList.filter(
          (card) => card.isUserDefined || card.isPatientDeck || card.isEditableTemplate
        );
      default:
        // For unknown filter options, return all cards
        return cardList;
    }
  }
}
